import { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { track, EVENTS } from '@/lib/telemetry';
import {
  Upload, Save, Download, FileSpreadsheet, Trash2, CheckCircle, Clock,
  ArrowRight, AlertTriangle, Wand2, ChevronRight, Bookmark, Zap, ArrowDownUp
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Templates
const TEMPLATES = {
  SAP: {
    Materials: 'material_number,product_name,material_group,hs_tariff,production_qty,uom\nMAT-001,Hot-Rolled Steel Coil,Steel,7208.10,500,TON\nMAT-002,Aluminum Billet 6061,Aluminum,7601.10,300,TON\nMAT-003,Portland Cement CEM I,Cement,2523.29,1200,TON',
    Customers: 'customer_id,company_name,country_code,industry_sector,email_address\n10001,Thyssenkrupp Steel Europe AG,DE,Steel,info@thyssenkrupp.com\n10002,BASF SE,DE,Chemicals,contact@basf.com\n10003,Heidelberg Materials AG,DE,Cement,info@heidelberg.com',
    Invoices: 'invoice_id,energy_source,kwh_consumed,unit,total_eur,invoice_date,vendor_name\nINV-001,Electricity,45000,kWh,12500,2025-01-31,E.ON SE\nINV-002,Natural Gas,28000,kWh,8900,2025-02-28,Uniper SE\nINV-003,Diesel,15000,L,5600,2025-03-15,Shell Deutschland',
  },
  DATEV: {
    Materials: 'Materialnr,Bezeichnung,Warengruppe,Zolltarif,Monatsmenge,Einheit\nMAT-101,Stahlblech warmgewalzt,Stahl,7208.10,450,TON\nMAT-102,Aluminium Barren,Aluminium,7601.10,280,TON\nMAT-103,Portlandzement,Zement,2523.29,1100,TON',
    Customers: 'Kundennummer,Firmenname,Land,Branche,EMail\n20001,ArcelorMittal Europe,DE,Stahl,info@arcelormittal.com\n20002,Heidelberg Materials,DE,Zement,info@heidelberg.com\n20003,SABIC Polymers,SA,Kunststoff,contact@sabic.com',
    Invoices: 'Belegnr,Energieart,Verbrauch,Einheit,Betrag_EUR,Datum,Lieferant\nRE-001,Strom,42000,kWh,11500,2025-01-31,E.ON SE\nRE-002,Erdgas,25000,kWh,7800,2025-02-28,Uniper SE\nRE-003,Diesel,12000,L,4900,2025-03-15,Shell',
  },
};

const TARGET_FIELDS = {
  Materials: [
    { key: 'name', label: 'Product Name', required: true },
    { key: 'sector', label: 'Sector' },
    { key: 'hs_code', label: 'HS Code' },
    { key: 'monthly_production', label: 'Monthly Production', numeric: true },
    { key: 'unit', label: 'Unit' },
  ],
  Customers: [
    { key: 'name', label: 'Company Name', required: true },
    { key: 'country', label: 'Country' },
    { key: 'sector', label: 'Sector' },
    { key: 'email', label: 'Email' },
  ],
  Invoices: [
    { key: 'energy_type', label: 'Energy Type', required: true },
    { key: 'consumption', label: 'Consumption', numeric: true, energy: true },
    { key: 'unit', label: 'Unit' },
    { key: 'cost', label: 'Cost (EUR)', numeric: true },
    { key: 'date', label: 'Invoice Date' },
    { key: 'supplier_name', label: 'Supplier Name' },
  ],
};

const ENERGY_UNITS = ['kWh', 'MWh', 'm3', 'liters', 'tons'];
const UNIT_FACTORS = { kWh: 1, MWh: 1000, m3: 10.55, liters: 10.0, tons: 8141 };

const KEYWORD_MAP = {
  name: ['name', 'bezeichnung', 'description', 'product', 'material', 'firma', 'firmenname', 'company', 'kunde', 'supplier', 'customer', 'vendor', 'produktname', 'artikelbezeichnung', 'nom_produit', 'designation', 'produit', 'libelle', 'nombre_producto', 'producto', 'articulo', 'denominacion', 'nome_prodotto', 'prodotto', 'denominazione', 'nom', 'nombre', 'nome', 'nome_produto', 'designacao', 'artigo', 'descricao', 'urun_adi', 'urun_ismi', 'malzeme', 'tanim', 'aciklama', 'اسم_المنتج', 'المنتج', 'الصنف', 'الوصف', '产品名称', '产品', '商品', '描述', '名称'],
  sector: ['sector', 'branche', 'industry', 'group', 'category', 'warengruppe', 'material_group'],
  hs_code: ['hs', 'tariff', 'zolltarif', 'commodity', 'hscode', 'hs_tariff'],
  monthly_production: ['production', 'quantity', 'menge', 'volume', 'output', 'monatsmenge', 'production_qty'],
  unit: ['unit', 'uom', 'einheit', 'measure', 'unite', 'mesure', 'unidad', 'medida', 'unita', 'misura', 'mass', 'unidade', 'birim', 'olcu', 'litro', 'litre'],
  country: ['country', 'land', 'nation', 'region', 'country_code', 'herkunftsland', 'pays', 'origine', 'pais', 'origen', 'paese', 'nazione', 'origem', 'ulke', 'mensei', 'kaynak_ulke'],
  email: ['email', 'mail', 'e-mail', 'email_address'],
  energy_type: ['energy', 'type', 'energie', 'energieart', 'fuel', 'energy_source', 'source', 'strom', 'gas', 'type_energie', 'electricite', 'tipo_energia', 'electricidad', 'elettricita', 'eletricidade', 'enerji_turu', 'enerji', 'elektrik', 'tip'],
  consumption: ['consumption', 'verbrauch', 'usage', 'kwh', 'consumed', 'kwh_consumed', 'menge', 'quantite', 'conso', 'consommation', 'cantidad', 'consumo', 'quantita', 'volumen', 'volume', 'quantidade', 'qtd', 'tuketim', 'miktar', 'hacim', 'الاستهلاك', 'الكمية', 'الحجم', '消耗', '数量', '用量', '消费', '用电量'],
  cost: ['cost', 'kosten', 'price', 'preis', 'amount', 'betrag', 'umsatz', 'total', 'total_eur', 'betrag_eur', 'eur', 'gesamt', 'cout', 'montant', 'prix', 'total_ht', 'total_ttc', 'coste', 'importe', 'precio', 'monto', 'costo', 'importo', 'prezzo', 'totale', 'ammontare', 'custo', 'montante', 'preco', 'valor', 'maliyet', 'tutar', 'fiyat', 'toplam', 'bedel', 'التكلفة', 'المبلغ', 'السعر', 'الإجمالي', '成本', '金额', '价格', '总计', '费用'],
  date: ['date', 'datum', 'invoice_date', 'period', 'rechnungsdatum', 'date_facture', 'facture_date', 'date_doc', 'fecha_factura', 'fecha', 'fecha_emision', 'data_fattura', 'data_doc', 'data_emissione', 'data_fatura', 'emissao', 'fatura_tarihi', 'tarih', 'duzenleme_tarihi', 'belge_tarihi', 'تاريخ_الفاتورة', 'التاريخ', '发票日期', '日期', '单据日期', '开票日期'],
  supplier_name: ['vendor', 'lieferant', 'supplier', 'vendor_name', 'anbieter', 'verkaufer', 'fournisseur', 'prestataire', 'vendeur', 'livreur', 'proveedor', 'suministrador', 'distribuidor', 'fornitore', 'venditore', 'erogatore', 'fornecedor', 'prestador', 'tedarikci', 'satici', 'dagitici', 'saglayici', 'المورد', 'مزود', 'بائع', '供应商', '供货商', '厂家', '卖方'],
};

function autoMap(csvHeaders, dataType) {
  const fields = TARGET_FIELDS[dataType] || [];
  const mapping = {};
  const used = new Set();
  fields.forEach(f => {
    const keywords = KEYWORD_MAP[f.key] || [f.key];
    let best = null, bestScore = 0;
    csvHeaders.forEach(h => {
      if (used.has(h)) return;
      const lower = h.toLowerCase().replace(/[^a-z0-9]/g, '');
      keywords.forEach(kw => {
        const kwClean = kw.replace(/[^a-z0-9]/g, '');
        let score = lower === kwClean ? 100 : lower.includes(kwClean) ? 70 : (kwClean.includes(lower) && lower.length > 2) ? 50 : 0;
        if (score > bestScore) { bestScore = score; best = h; }
      });
    });
    mapping[f.key] = (best && bestScore >= 50) ? best : '_skip_';
    if (best && bestScore >= 50) used.add(best);
  });
  return mapping;
}

function transformRows(rows, mapping, unitConversions) {
  const errors = [];
  const transformed = rows.map((row, idx) => {
    const out = {};
    Object.entries(mapping).forEach(([targetKey, csvCol]) => {
      if (csvCol === '_skip_') { out[targetKey] = ''; return; }
      let val = row[csvCol] ?? '';
      // Apply unit conversion
      if (unitConversions[targetKey] && unitConversions[targetKey] !== 'kWh' && val) {
        try {
          const numVal = parseFloat(val);
          if (!isNaN(numVal)) {
            const factor = UNIT_FACTORS[unitConversions[targetKey]] || 1;
            val = String(Math.round(numVal * factor * 100) / 100);
          }
        } catch (e) { console.warn('Unit conversion failed', e); }
      }
      out[targetKey] = val;
    });
    return out;
  });
  transformed.forEach((row, idx) => {
    Object.entries(row).forEach(([key, val]) => {
      const fieldDef = Object.values(TARGET_FIELDS).flat().find(f => f.key === key);
      if (fieldDef?.numeric && val && isNaN(Number(val)))
        errors.push({ row: idx + 1, field: key, value: val, message: `"${val}" is not a number` });
      if (fieldDef?.required && !val)
        errors.push({ row: idx + 1, field: key, value: '', message: 'Required field is empty' });
    });
  });
  return { transformed, errors };
}

function parseCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = lines.slice(1).filter(l => l.trim()).map(line => {
    const vals = line.split(',').map(v => v.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  });
  return { headers, rows };
}

function downloadTemplate(system, type) {
  const csv = TEMPLATES[system]?.[type] || '';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `${system.toLowerCase()}_${type.toLowerCase()}_template.csv`; a.click(); URL.revokeObjectURL(url);
}

// ==================== COMPONENT ====================

export default function ImportData() {
  const [dataType, setDataType] = useState('Materials');
  const [sourceSystem, setSourceSystem] = useState('SAP');
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState('');
  const [step, setStep] = useState('upload');
  const [mapping, setMapping] = useState({});
  const [unitConversions, setUnitConversions] = useState({});
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [saveProfileOpen, setSaveProfileOpen] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [appliedProfile, setAppliedProfile] = useState(null);
  const fileRef = useRef(null);

  const fetchHistory = useCallback(() => { axios.get(`${API}/csv-import`).then(r => setHistory(r.data)).catch(() => {}); }, []);
  const fetchProfiles = useCallback(() => { axios.get(`${API}/mapping-profiles`).then(r => setProfiles(r.data)).catch(() => {}); }, []);
  useEffect(() => { fetchHistory(); fetchProfiles(); }, [fetchHistory, fetchProfiles]);

  const reset = () => {
    setParsed(null); setFileName(''); setStep('upload'); setMapping({}); setUnitConversions({});
    setPreview([]); setErrors([]); setMessage(''); setAppliedProfile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const matchingProfiles = profiles.filter(p => p.source_system === sourceSystem && p.data_type === dataType);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name); setMessage('');
    track(EVENTS.CSV_UPLOADED, { data_type: dataType, source_system: sourceSystem, size_kb: Math.round((file.size || 0) / 1024) });
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = parseCSV(ev.target.result);
      setParsed(result);
      // Check for matching profiles
      if (matchingProfiles.length > 0) {
        // Don't auto-apply, let user choose
      }
      const auto = autoMap(result.headers, dataType);
      setMapping(auto);
      setUnitConversions({});
      setStep('mapping');
    };
    reader.readAsText(file);
  };

  const applyProfile = (profile) => {
    setMapping(profile.column_mapping || {});
    setUnitConversions(profile.unit_conversions || {});
    setAppliedProfile(profile.name);
  };

  const handleConfirmMapping = () => {
    if (!parsed) return;
    const { transformed, errors: errs } = transformRows(parsed.rows, mapping, unitConversions);
    setPreview(transformed);
    setErrors(errs);
    setStep('preview');
  };

  const handleSave = async () => {
    if (preview.length === 0) return;
    setSaving(true); setMessage('');
    try {
      const res = await axios.post(`${API}/csv-import/mapped`, {
        source_system: sourceSystem, data_type: dataType,
        mapped_data: preview, mapping, unit_conversions: unitConversions
      });
      setMessage(res.data.message);
      setStep('done');
      fetchHistory();
    } catch (e) { setMessage(e.response?.data?.detail || 'Save failed.'); }
    setSaving(false);
  };

  const handleSaveProfile = async () => {
    if (!profileName.trim()) return;
    await axios.post(`${API}/mapping-profiles`, {
      name: profileName.trim(), source_system: sourceSystem, data_type: dataType,
      column_mapping: mapping, unit_conversions: unitConversions
    });
    setSaveProfileOpen(false); setProfileName('');
    fetchProfiles();
  };

  const handleDeleteProfile = async (id) => { await axios.delete(`${API}/mapping-profiles/${id}`); fetchProfiles(); };
  const handleDeleteImport = async (id) => { await axios.delete(`${API}/csv-import/${id}`); fetchHistory(); };

  const fields = TARGET_FIELDS[dataType] || [];
  const STEPS = ['upload', 'mapping', 'preview', 'done'];

  return (
    <div data-testid="import-data-page" className="space-y-6 max-w-5xl">
      <div className="cp-page-header">
        <h1 className="cp-page-title flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-accent" /> Smart CSV Import
        </h1>
        {step !== 'upload' && <Button variant="outline" size="sm" onClick={reset} data-testid="import-reset-btn">Start Over</Button>}
      </div>

      {/* Steps */}
      <div className="flex items-center gap-2 text-xs" data-testid="import-steps">
        {STEPS.map((s, i) => (
          <span key={s} className={`flex items-center gap-1 ${step === s ? 'text-primary font-semibold' : i < STEPS.indexOf(step) ? 'text-green-600' : 'text-muted-foreground'}`}>
            <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center ${step === s ? 'bg-primary text-white' : i < STEPS.indexOf(step) ? 'bg-green-600 text-white' : 'bg-muted'}`}>{i + 1}</span>
            {['Upload', 'Map Fields', 'Preview', 'Done'][i]}
            {i < 3 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
          </span>
        ))}
      </div>

      {/* ===== STEP 1: UPLOAD ===== */}
      {step === 'upload' && (
        <Card className="p-6 animate-fade-in" data-testid="step-upload">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div>
              <Label>Data Type</Label>
              <Select value={dataType} onValueChange={v => { setDataType(v); reset(); }}>
                <SelectTrigger data-testid="import-data-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Materials">Materials (Products)</SelectItem>
                  <SelectItem value="Customers">Customers (Suppliers)</SelectItem>
                  <SelectItem value="Invoices">Invoices</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Source System</Label>
              <Select value={sourceSystem} onValueChange={v => { setSourceSystem(v); reset(); }}>
                <SelectTrigger data-testid="import-source-system"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SAP">SAP</SelectItem>
                  <SelectItem value="DATEV">DATEV</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 mb-5">
            <Button variant="outline" size="sm" onClick={() => downloadTemplate('SAP', dataType)} data-testid="download-sap-template"><Download className="w-3.5 h-3.5 mr-1.5" /> SAP Template</Button>
            <Button variant="outline" size="sm" onClick={() => downloadTemplate('DATEV', dataType)} data-testid="download-datev-template"><Download className="w-3.5 h-3.5 mr-1.5" /> DATEV Template</Button>
          </div>
          <div className="cp-upload-zone" onClick={() => fileRef.current?.click()} data-testid="csv-upload-zone">
            <Upload className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-sm font-medium">{fileName || 'Click to select CSV file'}</p>
            <p className="text-xs text-muted-foreground mt-1">.csv files only</p>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} data-testid="csv-file-input" />
          </div>
        </Card>
      )}

      {/* ===== STEP 2: MAPPING ===== */}
      {step === 'mapping' && parsed && (
        <Card className="p-6 animate-fade-in" data-testid="step-mapping">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-heading font-semibold">Field Mapping</h3>
            </div>
            {appliedProfile && <span className="cp-badge-green text-xs flex items-center gap-1"><Bookmark className="w-3 h-3" /> Profile: {appliedProfile}</span>}
          </div>

          {/* Saved Profiles */}
          {matchingProfiles.length > 0 && !appliedProfile && (
            <div className="mb-4 p-3 rounded-lg bg-accent/5 border border-accent/20" data-testid="saved-profiles-notice">
              <p className="text-xs font-semibold text-accent mb-2 flex items-center gap-1"><Bookmark className="w-3.5 h-3.5" /> Saved mapping profiles available</p>
              <div className="flex flex-wrap gap-2">
                {matchingProfiles.map(p => (
                  <Button key={p.id} variant="outline" size="sm" onClick={() => applyProfile(p)} data-testid={`apply-profile-${p.id}`} className="text-xs">
                    <Bookmark className="w-3 h-3 mr-1" /> {p.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground mb-4">Map CSV columns to target fields. Adjust any mapping using the dropdowns.</p>

          <div className="space-y-3">
            {fields.map(f => {
              const current = mapping[f.key] || '_skip_';
              return (
                <div key={f.key} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50" data-testid={`mapping-row-${f.key}`}>
                  <div className="w-36 flex-shrink-0">
                    <p className="text-sm font-medium">{f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}</p>
                    <div className="flex gap-1">
                      {f.numeric && <span className="text-[10px] text-muted-foreground">numeric</span>}
                      {f.energy && <span className="text-[10px] text-amber-500 flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" />energy</span>}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <Select value={current} onValueChange={v => setMapping(prev => ({ ...prev, [f.key]: v }))}>
                    <SelectTrigger className="flex-1" data-testid={`mapping-select-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_skip_">-- Skip --</SelectItem>
                      {parsed.headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {/* Unit conversion for energy fields */}
                  {f.energy && current !== '_skip_' && (
                    <div className="flex items-center gap-1.5 flex-shrink-0" data-testid={`unit-conv-${f.key}`}>
                      <ArrowDownUp className="w-3.5 h-3.5 text-amber-500" />
                      <Select value={unitConversions[f.key] || 'kWh'} onValueChange={v => setUnitConversions(prev => ({ ...prev, [f.key]: v }))}>
                        <SelectTrigger className="w-24 h-8 text-xs" data-testid={`unit-select-${f.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ENERGY_UNITS.map(u => <SelectItem key={u} value={u}>{u}{u !== 'kWh' ? ' → kWh' : ' (default)'}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {current !== '_skip_' && !f.energy && (
                    <span className="text-[10px] text-green-600 flex-shrink-0 flex items-center gap-0.5"><CheckCircle className="w-3 h-3" /></span>
                  )}
                </div>
              );
            })}
          </div>

          {/* kWh default notice for energy fields */}
          {fields.some(f => f.energy && mapping[f.key] !== '_skip_' && (!unitConversions[f.key] || unitConversions[f.key] === 'kWh')) && (
            <div className="mt-3 flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/10 text-xs text-amber-700 dark:text-amber-400" data-testid="kwh-default-notice">
              <Zap className="w-3.5 h-3.5 flex-shrink-0" />
              Energy fields default to <strong>kWh</strong>. Change the unit dropdown if your data uses MWh, m3, liters, or tons.
            </div>
          )}

          <details className="mt-4">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Raw CSV (first 3 rows)</summary>
            <div className="mt-2 cp-table-wrapper overflow-x-auto">
              <Table>
                <TableHeader><TableRow>{parsed.headers.map(h => <TableHead key={h} className="text-xs">{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>{parsed.rows.slice(0, 3).map((row, i) => <TableRow key={i}>{parsed.headers.map(h => <TableCell key={h} className="text-xs">{row[h]}</TableCell>)}</TableRow>)}</TableBody>
              </Table>
            </div>
          </details>

          <Button onClick={handleConfirmMapping} className="mt-5 bg-primary hover:bg-primary/90" data-testid="confirm-mapping-btn">
            <Wand2 className="w-4 h-4 mr-2" /> Confirm & Preview
          </Button>
        </Card>
      )}

      {/* ===== STEP 3: PREVIEW ===== */}
      {step === 'preview' && (
        <Card className="p-6 animate-fade-in" data-testid="step-preview">
          <h3 className="text-sm font-heading font-semibold mb-1">Transformed Data</h3>
          <p className="text-xs text-muted-foreground mb-4">
            {preview.length} records → <span className="font-semibold text-primary">{dataType === 'Materials' ? 'products' : dataType === 'Customers' ? 'suppliers' : 'invoices'}</span>
            {Object.keys(unitConversions).filter(k => unitConversions[k] !== 'kWh').length > 0 && (
              <span className="ml-2 text-amber-500">( unit conversion applied → kWh)</span>
            )}
          </p>

          {errors.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="validation-errors">
              <p className="text-xs font-semibold text-amber-600 mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {errors.length} warning(s)</p>
              <ul className="text-[11px] text-amber-700 dark:text-amber-400 space-y-0.5">
                {errors.slice(0, 5).map((e, i) => <li key={i}>Row {e.row}: {e.field} — {e.message}</li>)}
                {errors.length > 5 && <li>...{errors.length - 5} more</li>}
              </ul>
            </div>
          )}

          <div className="cp-table-wrapper overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{fields.map(f => <TableHead key={f.key} className="text-xs">{f.label}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {preview.slice(0, 5).map((row, i) => (
                  <TableRow key={i} data-testid={`preview-row-${i}`}>
                    {fields.map(f => (
                      <TableCell key={f.key} className={`text-sm ${!row[f.key] && f.required ? 'text-red-500 italic' : ''}`}>
                        {row[f.key] || (f.required ? 'MISSING' : '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {preview.length > 5 && <p className="text-xs text-muted-foreground mt-2">...{preview.length - 5} more rows</p>}

          {message && (
            <div className={`mt-4 flex items-center gap-2 p-3 rounded-lg text-sm ${message.includes('Imported') || message.includes('records') ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`} data-testid="import-message">
              <CheckCircle className="w-4 h-4 flex-shrink-0" /> {message}
            </div>
          )}

          <div className="flex gap-2 mt-5">
            <Button variant="outline" onClick={() => setStep('mapping')} data-testid="back-to-mapping-btn">Back</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-mapped-btn">
              {saving ? <span className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" /> : <Save className="w-4 h-4 mr-2" />}
              Import {preview.length} Records
            </Button>
            <Button variant="outline" onClick={() => setSaveProfileOpen(true)} data-testid="save-profile-btn">
              <Bookmark className="w-4 h-4 mr-1.5" /> Save Profile
            </Button>
          </div>
        </Card>
      )}

      {/* ===== STEP 4: DONE ===== */}
      {step === 'done' && (
        <Card className="p-8 text-center animate-scale-in" data-testid="step-done">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <h3 className="text-lg font-heading font-bold mb-1">Import Complete</h3>
          <p className="text-sm text-muted-foreground mb-4">{message}</p>
          <div className="flex gap-2 justify-center">
            <Button onClick={reset} className="bg-primary hover:bg-primary/90" data-testid="import-another-btn">Import Another</Button>
            {!appliedProfile && (
              <Button variant="outline" onClick={() => setSaveProfileOpen(true)} data-testid="save-profile-done-btn">
                <Bookmark className="w-4 h-4 mr-1.5" /> Save Mapping Profile
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Saved Profiles Section */}
      {profiles.length > 0 && (
        <div className="cp-chart-card animate-fade-in" data-testid="profiles-section">
          <h3 className="text-sm font-heading font-semibold mb-3 flex items-center gap-2">
            <Bookmark className="w-4 h-4 text-accent" /> Saved Mapping Profiles
          </h3>
          <div className="cp-table-wrapper">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>System</TableHead><TableHead>Type</TableHead><TableHead>Fields</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {profiles.map(p => (
                  <TableRow key={p.id} data-testid={`profile-row-${p.id}`}>
                    <TableCell className="font-medium text-sm">{p.name}</TableCell>
                    <TableCell><span className="cp-badge-indigo text-xs">{p.source_system}</span></TableCell>
                    <TableCell className="text-sm">{p.data_type}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{Object.keys(p.column_mapping || {}).filter(k => p.column_mapping[k] !== '_skip_').length} mapped</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteProfile(p.id)} className="text-destructive hover:text-destructive" data-testid={`delete-profile-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Import History */}
      <div className="cp-chart-card animate-fade-in" data-testid="import-history-section">
        <h3 className="text-sm font-heading font-semibold mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" /> Import History
        </h3>
        <div className="cp-table-wrapper">
          <Table>
            <TableHeader><TableRow>
              <TableHead>System</TableHead><TableHead>Type</TableHead><TableHead>Mapped</TableHead><TableHead className="text-right">Records</TableHead><TableHead className="text-right">Date</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                  <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 opacity-30" /> No imports yet
                </TableCell></TableRow>
              ) : history.map(h => (
                <TableRow key={h.id} data-testid={`history-row-${h.id}`}>
                  <TableCell><span className="cp-badge-indigo text-xs">{h.source_system}</span></TableCell>
                  <TableCell className="font-medium text-sm">{h.data_type}</TableCell>
                  <TableCell>{h.mapped ? <span className="cp-badge-green text-xs">Mapped</span> : <span className="text-xs text-muted-foreground">Raw</span>}</TableCell>
                  <TableCell className="text-right font-mono">{h.record_count}</TableCell>
                  <TableCell className="text-right text-xs text-muted-foreground">{new Date(h.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteImport(h.id)} className="text-destructive hover:text-destructive" data-testid={`delete-import-${h.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Save Profile Dialog */}
      <Dialog open={saveProfileOpen} onOpenChange={setSaveProfileOpen}>
        <DialogContent data-testid="save-profile-dialog">
          <DialogHeader><DialogTitle>Save Mapping Profile</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Save this mapping for {sourceSystem} / {dataType} to reuse in future imports.</p>
          <div className="mt-3">
            <Label>Profile Name</Label>
            <Input value={profileName} onChange={e => setProfileName(e.target.value)} placeholder={`e.g. ${sourceSystem} ${dataType} default`} data-testid="profile-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveProfileOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProfile} className="bg-primary hover:bg-primary/90" data-testid="save-profile-confirm-btn">
              <Bookmark className="w-4 h-4 mr-2" /> Save Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
